import { useMutation } from "@apollo/client";
import { Box, Button, Grid, TextField } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import useText from "constants/text";
import { InfoBarContext } from "contexts/InfoBarContext";
import { useRegistrationContext } from "contexts/Registration";
import { useCountryCode } from "hooks/useCurrentUser";
import React, { ChangeEvent, useEffect, useState } from "react";
import { CLIENT_REQUEST_CREATION } from "../graphql/mutations";
import { useStyles } from "./styles";
import { StepSecondValidation } from "./validators";

interface IProps {
  onNext?: React.Dispatch<React.SetStateAction<number>>;
  onPrev?: React.Dispatch<React.SetStateAction<number>>;
}

interface IStepSecondValues {
  client_contact_firstname: string;
  client_contact_lastname: string;
  client_contact_job: string;
  client_contact_email: string;
  client_contact_phone_number: string;
}

const defaultValues: IStepSecondValues = {
  client_contact_firstname: "",
  client_contact_lastname: "",
  client_contact_job: "",
  client_contact_email: "",
  client_contact_phone_number: "",
};

export default function StepSecondOfRegister({ onNext, onPrev }: IProps) {
  const { text } = useText();
  const styles = useStyles();
  const [createClientRequest] = useMutation(CLIENT_REQUEST_CREATION);
  const { client } = useRegistrationContext();
  const [isFormSubmited, setIsFormSubmited] = useState(false);
  const [formValues, setFormValues] = useState(defaultValues);
  const { setInfo } = React.useContext(InfoBarContext);
  const countryCode = useCountryCode();

  useEffect(() => {
    setFormValues({
      client_contact_firstname: client?.client_contact_firstname,
      client_contact_lastname: client?.client_contact_lastname,
      client_contact_job: client?.client_contact_job,
      client_contact_email: client?.client_contact_email,
      client_contact_phone_number: client?.client_contact_phone_number,
    });
  }, []);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, client_contact_firstname: e.target.value });
  };

  const handleBack = () => {
    onPrev(0);
  };

  const onSubmit = ({ ...client }) => {
    createClientRequest({
      variables: {
        input: {
          client_company_name: client?.client_company_name,
          client_activity: client?.client_activity,
          client_phone_number: client?.client_contact_phone_number,
          client_address: {
            address: client?.client_address?.address,
            place_id: client?.client_address?.place_id,
          },
          client_contact_firstname: client?.client_contact_firstname,
          client_contact_lastname: client?.client_contact_lastname,
          client_contact_job: client?.client_contact_job,
          client_contact_phone_number: client?.client_contact_phone_number,
          client_contact_email: client?.client_contact_email,
        },
      },
    }).then((response) => {
      if (response.data.createClientRequest.error != null) {
        onNext(3);
      } else {
        onNext(2);
      }
    }).catch((error) => {
      setInfo({ type: "info", message: error.message });;
      });
  };


  const handleNext = () => {
    setIsFormSubmited(true);
    if (!StepSecondValidation.isValidSync(formValues)) return;
    onSubmit({ ...client, ...formValues });
  };

  const fieldsErrors = React.useMemo<Partial<Record<keyof IStepSecondValues, boolean>>>(() => {
    const errors: Partial<Record<keyof IStepSecondValues, boolean>> = {};
    if (isFormSubmited) {
      Object.keys(formValues).forEach((key) => {
        try {
          StepSecondValidation.validateSyncAt(key, formValues);
        } catch (error) {
          errors[key] = true;
        }
      });
      return errors;
    }
    return {};
  }, [isFormSubmited, formValues]); 

  return (
    <Grid
      container
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label={text.all.first_name}
          name="client_contact_firstname"
          type="text"
          placeholder={text.all.first_name}
          onChange={onChange}
          value={formValues?.client_contact_firstname}
          error={fieldsErrors.client_contact_firstname}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label={text.all.last_name}
          name="client_contact_lastname"
          type="text"
          placeholder={text.all.last_name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setFormValues({ ...formValues, client_contact_lastname: e.target.value });
          }}
          value={formValues?.client_contact_lastname}
          error={fieldsErrors.client_contact_lastname}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <PhoneField
        style={{width: 480}}
              countryCode={countryCode}
              fullWidth
              label={text.all.phone_number}
              value={formValues?.client_contact_phone_number}
              name="client_contact_phone_number"
              onValueChange={(value) => {
                setFormValues({ ...formValues, client_contact_phone_number: value });
              }}
              error={fieldsErrors.client_contact_phone_number}
              required
              type="tel"
              placeholder={text.all.phone_number_placeholder}
            />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label={text.all.post}
          name="client_contact_job"
          type="text"
          placeholder={text.all.post}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setFormValues({ ...formValues, client_contact_job: e.target.value });
          }}
          value={formValues?.client_contact_job}
          error={fieldsErrors.client_contact_job}
          required
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label={text.all.email}
          name="client_contact_email"
          placeholder={text.all.email}
          type="email"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setFormValues({ ...formValues, client_contact_email: e.target.value });
          }}
          value={formValues?.client_contact_email}
          error={fieldsErrors.client_contact_email}
          required
        />
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <div className={styles.buttons}>
          <Button className={styles.log} onClick={handleBack}>
            {text.all.previous}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            className={styles.log}
          >
            {text.all.sent}
          </Button>
        </div>
      </Box>
    </Grid>
  );
}

